<template>
  <div class="my-franchises-page" data-testid="my-franchises-page">
    <page-header
      :title="t('Pagamento e uso')"
      icon="pi pi-receipt"
    />

    <box>

      <!-- Ownership empty-state: GET /subscriptions/mine returned ZERO rows —
           the user does not own any franchise (ownership-scoped, not the staff
           permissions map). This is the ONLY case that replaces the table:
           there is genuinely nothing to list and nothing to subscribe. -->
      <no-franchise-empty-state
        v-if="showNoFranchiseEmpty"
        data-testid="no-franchise-empty-state-container"
      />

      <!-- Owning ANY franchise means the table renders — including when none of
           them is subscribed. The table carries a per-row 'Assinar' button
           (MyFranchisesTable, buttonCondition: no subscription_id AND setup
           paid|exempt), which is the only affordance that lets the user pick
           WHICH franchise to subscribe. Setup owed hides Assinar — POST
           /subscriptions/subscribe refuses with setup_fee_owed.
           A single generic CTA cannot: with three franchises it asks a question
           it has no way to answer.

           This replaced an OnboardingEmptyState branch that hid the whole table
           whenever every row lacked a subscription. That branch had already
           swallowed the table once before (T7b: a franchise granted grace with no
           subscription row lost its visible deadline) and was narrowed rather than
           removed. S12/M3 made the same shape routine rather than exotic: a cut
           subscription returns its row to subscription_id null, so cancelling a
           franqueado's only subscription dropped them into a placeholder with no
           way to choose a franchise. Narrowing a rule that keeps hiding rows the
           user needs is treating the symptom; the table always renders now. -->
      <template v-else>
        <cartao-da-implantacao />
        <setup-fee-pending-card
          v-for="r in pendingSetupFeeRows"
          :key="r.franchise_id"
          :franquia-id="r.franchise_id"
          :franchise-name="r.franchise_name"
        />
        <my-franchises-table
          :rows="displayRows"
          data-testid="my-franchises-table-container"
          @refresh="loadData"
          @edit-click="handleEditClick"
          @subscribe-click="handleSubscribeClick"
          @pay-click="handlePayClick"
          @pay-setup-click="handlePaySetupClick"
        />
      </template>
    </box>
  </div>
</template>


<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMySubscriptions } from '../../composables/useMySubscriptions'
import { useSubscriptionPayment } from '../../composables/useSubscriptionPayment'
import { useSetupFeeCheckout } from '../../composables/useSetupFeeCheckout'
import { hasPayableSetupCharge } from '../../utils/setupFeeStatus'
import { useDateFormatter } from '../../composables/useDateFormatter'
import { formatCredits } from '../assistant/formatCredits'
import { eventBus } from '../../eventBus'
import { log } from '../../utils/DebugLogger'
import PageHeader from '../../ui/PageHeader.vue'
import NoFranchiseEmptyState from './NoFranchiseEmptyState.vue'
import SetupFeePendingCard from './SetupFeePendingCard.vue'
import CartaoDaImplantacao from '../implantacao/CartaoDaImplantacao.vue'
import MyFranchisesTable from './MyFranchisesTable.vue'


const { t, n } = useI18n()
const { rows, fetchMySubscriptions } = useMySubscriptions()
const { payAndCheckout } = useSubscriptionPayment()
const { checkoutAndRedirect } = useSetupFeeCheckout()
const { fromApiDateTimeFormat, formatDate } = useDateFormatter()

// Shared predicate (src/utils/setupFeeStatus): the card renders only while a
// charge is payable — an obligation satisfied by staff must drop it even if a
// stale pending-charge flag lingers in the row.
const pendingSetupFeeRows = computed(() => {
  return rows.value.filter(r => hasPayableSetupCharge(r))
})


/**
 * showNoFranchiseEmpty — true when GET /subscriptions/mine returns ZERO rows:
 * the user does not OWN any franchise (subscriptions/mine.php resolves by
 * ownership, not the staff permissions map — deliberately kept that way so a
 * franqueado without a live subscription can still reach this page and pay). Distinct from
 * showOnboarding (owns franchises, never subscribed): here there is nothing
 * to subscribe to, so the page must say the real reason instead of rendering
 * an empty table with a 'Nova assinatura' button that has no target.
 */
const showNoFranchiseEmpty = computed<boolean>(() => rows.value.length === 0)

/**
 * Pre-mapped display rows — augment each raw row with:
 *   plan_display  — plan_name or '—' when absent
 *   grace_display — grace_ends_at + grace_days_remaining (the AUTHORITY-sourced
 *                   fields, #24) formatted as "date (N dias)" for grace rows,
 *                   '—' otherwise. NOT grace_until — that column is only the
 *                   stale franchises_subscriptions display dual-write.
 *                   t() wraps only the static word 'dia'/'dias' — the number
 *                   is plain JS concatenation, not a named-param
 *                   interpolation, so a not-yet-translated key never flashes
 *                   raw '{days}'. Singular/plural picked in plain JS (matches
 *                   PDVEntregasNotifyModal.vue's existing pattern) — "(1 dias)"
 *                   was agrammatical.
 */
// Same gateway labels BillingThreeSixtyTable shows staff.
function providerLabel(provider: string | null | undefined): string {
  if (!provider) return ''
  const map: Record<string, string> = {
    asaas: 'Asaas',
    stripe: 'Stripe',
    fake: 'Fake',
  }
  return map[provider.toLowerCase()] ?? (provider.charAt(0).toUpperCase() + provider.slice(1))
}

const displayRows = computed(() =>
  rows.value.map((row) => ({
    ...row,
    plan_display: row.plan_name ?? '—',
    // Billing-360 parity fields (2026-08-20): value, gateway, credits and
    // setup-fee amount rendered exactly like BillingThreeSixtyTable.
    price_display: row.price_cents != null
      ? n(row.price_cents / 100, { style: 'currency', currency: row.currency || 'BRL' })
      : (row.currency ?? ''),
    provider_display: providerLabel(row.provider),
    credits_display: `${formatCredits(row.credits_balance_micro)} ${t('cr')}`,
    setup_fee_display: row.setup_fee_amount_cents != null
      ? n(row.setup_fee_amount_cents / 100, { style: 'currency', currency: row.setup_fee_currency || 'BRL' })
      : '',
    grace_display:
      row.status === 'grace' && row.grace_ends_at && row.grace_days_remaining != null
        ? `${formatDate(fromApiDateTimeFormat(row.grace_ends_at))} (${row.grace_days_remaining} ${row.grace_days_remaining === 1 ? t('dia') : t('dias')})`
        : '—',
  })),
)

function handleSubscribeClick(franquia_id: number): void {
  log('MyFranchisesPage', 'handleSubscribeClick', 'Abrindo modal de assinatura para franquia', { franquia_id })
  eventBus.emit('modal.subscribe.open', { franquia_id })
}


/**
 * handlePayClick — the way OUT of carência.
 *
 * Until this existed the page could only offer 'Assinar', and only to franchises
 * that had never subscribed: a franchise inside its grace window had a deadline
 * on screen and no button to meet it.
 *
 * Three outcomes, all explicit:
 *   · redirected → a real gateway checkout page; the browser is already leaving.
 *   · not redirected → the checkout_url points back at our own API (GatewayTest).
 *     We stay on the page — window.location on an internal URL destroys the SPA
 *     (S8R2-01) — say the charge is waiting, and refresh so the row reflects it.
 *   · error → the backend's NAMED refusal, surfaced verbatim. It reaches us as a
 *     thrown Error only because useSubscriptionPayment reads the ENVELOPE: the
 *     refusal itself arrives as HTTP 200.
 */
async function handlePayClick(franquia_id: number): Promise<void> {
  log('MyFranchisesPage', 'handlePayClick', 'Gerando cobrança da assinatura', { franquia_id })
  try {
    const { redirected, dados } = await payAndCheckout(franquia_id)
    if (redirected) {
      return
    }
    // 'globalMessage.show' + type, NOT 'globalMessage.info': GlobalMessages.vue
    // listens for show/error/success/warning only, so an 'info' emit is silently
    // dropped. (Six other emits in src/ already are — pre-existing, not ours to
    // fix here, but not ours to join either.)
    eventBus.emit('globalMessage.show', {
      type: 'info',
      message: dados.reused
        ? t('Retomando a cobrança pendente desta assinatura.')
        : t('Cobrança gerada. Conclua o pagamento para reativar a assinatura.'),
    })
    await loadData()
  } catch (err) {
    log('MyFranchisesPage', 'handlePayClick', 'Erro ao gerar cobrança', err)
    eventBus.emit('globalMessage.error', { message: (err as Error)?.message })
  }
}

/**
 * handlePaySetupClick — the setup-fee twin of handlePayClick, fed by the
 * per-row 'Pagar implantação' button (condition: has_pending_setup_fee).
 * Same three outcomes: external URL → the browser is already leaving;
 * internal URL (GatewayTest) → stay on the page and say the charge is
 * waiting; error → the backend's NAMED refusal verbatim (useSetupFeeCheckout
 * reads the envelope via throwOnEnvelopeFailure).
 */
/**
 * handlePaySetupClick — the setup-fee twin of handlePayClick. The button only
 * renders when a charge exists (canPaySetup: has_pending_setup_fee), so the
 * click goes STRAIGHT to the payment link — no modal detour (user decision
 * 2026-08-20). Same three outcomes as the subscription door: external URL →
 * the browser is already leaving; internal (GatewayTest) → stay and announce;
 * error (e.g. the charge expired between render and click) → the backend's
 * NAMED refusal verbatim.
 */
async function handlePaySetupClick(franquia_id: number): Promise<void> {
  log('MyFranchisesPage', 'handlePaySetupClick', 'Abrindo checkout da taxa de implantação', { franquia_id })
  try {
    const { redirected } = await checkoutAndRedirect(franquia_id)
    if (redirected) {
      return
    }
    eventBus.emit('globalMessage.show', {
      type: 'info',
      message: t('Cobrança de implantação aguardando pagamento no provedor.'),
    })
    await loadData()
  } catch (err) {
    log('MyFranchisesPage', 'handlePaySetupClick', 'Erro no checkout da implantação', err)
    eventBus.emit('globalMessage.error', { message: (err as Error)?.message })
    await loadData()
  }
}


function handleEditClick(value: unknown): void {
  log('MyFranchisesPage', 'handleEditClick', 'Abrindo Billing 360 da franquia', { value })
  // Look up the full row by subscription_id or franchise_id
  const row = rows.value.find((r) => r.subscription_id === value || r.franchise_id === value)
  const fid = row?.franchise_id ?? (typeof value === 'number' ? value : null)
  if (fid) {
    eventBus.emit('modal.billing-360.open', {
      franquiaId: fid,
      franchiseName: row?.franchise_name ?? '',
      isStaff: false,
    })
  } else {
    eventBus.emit('modal.subscriptionDetail.open', { subscription_id: value, ...(row ?? {}) })
  }
}


async function loadData(): Promise<void> {
  try {
    await fetchMySubscriptions()
  } catch (err) {
    log('MyFranchisesPage', 'loadData', 'Erro ao carregar assinaturas', err)
    eventBus.emit('globalMessage.error', { message: (err as Error)?.message })
  }
}

function onSubscriptionChanged(): void {
  log('MyFranchisesPage', 'onSubscriptionChanged', 'subscription.changed — re-fetching list')
  void loadData()
}

onMounted(async () => {
  eventBus.on('subscription.changed', onSubscriptionChanged)
  await loadData()
})

onBeforeUnmount(() => {
  eventBus.off('subscription.changed', onSubscriptionChanged)
})
</script>
